import { Image, Text, View } from '@react-pdf/renderer';

import type { Scan } from '../../../../api/generated/insights.schemas';
import { styles } from '../styles';

interface VisibilitySectionProps {
  scan: Scan;
  radarImage: string;
}

function formatNumber(value: unknown, fallback = '—'): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return String(value);
}

export default function VisibilitySection({
  scan,
  radarImage,
}: VisibilitySectionProps) {
  const std = scan.scores?.standardized;
  const visibilityPct = std?.visibility?.value;

  const mentionsCount = scan.mentions?.mentionedInNumberOfQueries?.value;
  const totalQueries = scan.numberOfQueries;

  const selfRanking = scan.rankings?.list?.find(
    (item) => item.brand?.isSelf === true,
  );
  const rankingValue = selfRanking?.rank?.value;

  const citationsTotal = scan.citations?.ownBrandTotal?.value;
  const sentimentPct = std?.sentiment?.value;

  return (
    <View>
      <Text style={styles.sectionTitle}>AI Visibility Index</Text>
      <Text style={styles.sectionSubtitle}>
        How visible the brand is across the selected AI platform, based on
        mentions, ranking, citations and sentiment.
      </Text>

      <View style={styles.aiviRow}>
        <View style={styles.aiviScoreBlock}>
          <Text style={styles.aiviScoreNumber}>
            {formatNumber(visibilityPct, '—')}
            <Text style={{ fontSize: 16, color: '#94a3b8' }}> / 100</Text>
          </Text>
          <Text style={styles.aiviScoreLabel}>AIVI score</Text>
        </View>

        <Image src={radarImage} style={styles.aiviChart} />
      </View>

      <View style={styles.aiviTiles}>
        <View style={styles.aiviTile}>
          <Text style={styles.aiviTileLabel}>Mentions</Text>
          <Text style={styles.aiviTileValue}>
            {formatNumber(mentionsCount, '0')}
            {typeof totalQueries === 'number' ? (
              <Text style={{ fontSize: 12, color: '#94a3b8' }}>
                {` / ${totalQueries}`}
              </Text>
            ) : null}
          </Text>
        </View>

        <View style={styles.aiviTile}>
          <Text style={styles.aiviTileLabel}>Competitor ranking</Text>
          <Text style={styles.aiviTileValue}>
            {typeof rankingValue === 'number' && rankingValue > 0
              ? `#${rankingValue}`
              : '—'}
          </Text>
        </View>

        <View style={styles.aiviTile}>
          <Text style={styles.aiviTileLabel}>Citations</Text>
          <Text style={styles.aiviTileValue}>
            {formatNumber(citationsTotal, '0')}
          </Text>
        </View>

        <View style={styles.aiviTile}>
          <Text style={styles.aiviTileLabel}>Positive sentiment</Text>
          <Text style={styles.aiviTileValue}>
            {typeof sentimentPct === 'number' ? `${sentimentPct}%` : '—'}
          </Text>
        </View>
      </View>
    </View>
  );
}
